/**
 * Custom hook to handle organization submission
 * Consolidates create and update org mutation logic
 */

import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { KYCStatus } from "@funded-here-interface/common/src/constant/enum";
import { ThanksPageType } from "../../../constants/enums";
import { navigateToThanksPage } from "../../ThanksPage/ThanksPageUtils";
import useCreateOrg from "../../../hooks/useCreateOrg";
import useUpdateOrg from "../../../hooks/useUpdateOrg";
import {
  buildBankObject,
  buildCreateOrgData,
  buildUpdateOrgData,
  OrgBizState,
  OrgBankState,
} from "../utils/orgDataBuilder";
import { DocumentsUpload } from "@funded-here-interface/common/src/constant/type";

interface UseOrgSubmissionParams {
  setIsLoading: (loading: boolean) => void;
  onError: (error: Error) => void;
}

/**
 * Hook to handle organization submission (create or update)
 */
export const useOrgSubmission = ({
  setIsLoading,
  onError,
}: UseOrgSubmissionParams) => {
  const navigate = useNavigate();
  const createOrgMutation = useCreateOrg();
  const updateOrgMutation = useUpdateOrg();

  /**
   * Submit organization (create or update based on KYC status)
   */
  const submitOrganization = useCallback(
    ({
      kycStatus,
      orgId,
      token,
      onBoardingUserType,
      bizState,
      bankState,
      documentsUpload,
    }: {
      kycStatus: string | null;
      orgId: string | null;
      token: string;
      onBoardingUserType: string;
      bizState: OrgBizState;
      bankState: OrgBankState;
      documentsUpload: DocumentsUpload;
    }) => {
      // Build bank object
      const bank = buildBankObject(bankState);

      // Get country code - bizState.countryISOCode already contains the ISO code
      // (converted from country name in BizProfile using countryNameToCodeMap)
      const countryCode = bizState.countryISOCode || null;

      setIsLoading(true);

      const baseParams = {
        bizState,
        bank,
        token,
        onBoardingUserType,
        countryCode,
        documentsUpload,
      };

      // If KYC is pending resubmission, update existing org
      if (kycStatus === KYCStatus.PENDING_RESUBMISSION) {
        const updateData = buildUpdateOrgData(baseParams);

        updateOrgMutation.mutate(
          {
            id: orgId?.toString() || "",
            data: updateData,
          },
          {
            onSuccess: () => {
              setIsLoading(false);
              navigateToThanksPage(navigate, ThanksPageType.RESUBMITTED);
            },
            onError: (e) => {
              setIsLoading(false);
              onError(e as Error);
            },
          }
        );
      }
      // Otherwise, create new org
      else {
        const createData = buildCreateOrgData(baseParams);

        createOrgMutation.mutate(createData, {
          onSuccess: () => {
            setIsLoading(false);
            navigateToThanksPage(navigate, ThanksPageType.ONBOARDING_SUPPLIER);
          },
          onError: (e) => {
            setIsLoading(false);
            onError(e as Error);
          },
        });
      }
    },
    [navigate, createOrgMutation, updateOrgMutation, setIsLoading, onError]
  );

  return { submitOrganization };
};
